import { useEffect, useRef, useState } from "react";
import {
  ABOUT_US_PATH,
  CATEGORY_PATH,
  CONTACT_US_PATH,
  HOME_PATH,
  LOGOUT_PATH,
  MANAGE_NOTIFICATION_PATH,
  NOTIFCATION_PATH,
  PROFILE_PATH,
  PUSH_NOTIFICATION_PATH,
  REGISTER_PATH,
  SUBSCRIPTION_PATH,
  MANAGE_SUBSCRIPTION_PLAN_PATH,
  TNC_PATH,
  REGISTER_GENERAL_PATH,
  HOME_SEARCH_PATH,
  LIST_USER_PATH,
  USER_MANAGEMENT_PATH,
  CONTACT_US_MANAGEMENT_PATH,
  ANALYTICS_MANAGEMENT_PATH,
  FAVOURITES_PATH,
  BASE_URL,
  BASE_APP_URL,
} from "../../utility/path";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import "../../navbar.css";
import { logoutAction } from "../../redux/slices/authSlice";
import { handleHrefRedirect, isMobile, logoutErrors } from "../../services/functions";
import useDynamicDimension from "../../services/useDynamicDimension";
import CustomIcon from "../CustomIcon";
import CustomImage from "../CustomImage";
import { hideAlert } from "../../redux/slices/alertSlice";
import useImageCacheLocalStorage from "../../services/useImageCacheLocalStorage";
import logo from "../../logo192.png";
import useOnScroll from "../../services/useOnScroll";
import { getTransactionStatusStyle } from "../../utility/colorUtils";
import { getRoleLabel } from "../../utility/authGuard";

/* ─────────────────────────────────────────────────────────────────────────────
   Helper: single nav item row for the mobile drawer
───────────────────────────────────────────────────────────────────────────── */
const DrawerItem = ({ iconClass, emoji, label, to, badge, danger, active, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={[
      "mob-nav__item",
      active  ? "mob-nav__item--active"  : "",
      danger  ? "mob-nav__item--danger"  : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <span className={`mob-nav__item-icon mob-nav__item-icon--${iconClass}`}>
      {emoji}
    </span>
    <span className="mob-nav__label">{label}</span>
    {badge != null && <span className="mob-nav__badge">{badge}</span>}
    <span className="mob-nav__chevron">›</span>
  </NavLink>
);

/* ─────────────────────────────────────────────────────────────────────────────
   NavBar component
───────────────────────────────────────────────────────────────────────────── */
const NavBar = () => {
  const [appVersion] = useState("Version 2.0.0.100085");
  const [isNavExpanded, setIsNavExpanded] = useState(false);

  const authData  = useSelector((state) => state?.auth?.data);
  const header    = useSelector((state) => state?.header?.data);
  const alert     = useSelector((state) => state?.alert);

  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const location    = useLocation();
  const screenSize  = useDynamicDimension();

  const { image }         = useImageCacheLocalStorage(authData?.profilePhoto || logo);
  const { pageYOffset }   = useOnScroll();
  const prevScrollpos     = useRef(0);

  const hideBackButton = location?.pathname === HOME_PATH || location?.pathname === HOME_SEARCH_PATH;
  const backToHomePath = location?.pathname === HOME_PATH ? HOME_SEARCH_PATH : HOME_PATH;

  const logoutRequiredFor = ["logout"];

  const roleLabel = getRoleLabel(authData);


  /* ── close drawer on route change ── */
  useEffect(() => {
    setIsNavExpanded(false);
  }, [location.pathname]);

  /* ── lock body scroll when drawer is open ── */
  useEffect(() => {
    document.body.style.overflow = isNavExpanded ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isNavExpanded]);

  /* ── logout error watcher ── */
  useEffect(() => {
    if (logoutErrors.includes(alert?.data?.message)) {
      dispatch(logoutAction({}));
      setTimeout(() => {
        dispatch(hideAlert({}));
        navigate(REGISTER_PATH);
      }, 1500);
    }
  }, [alert]);

  /* ── scroll hide/show header ── */
  useEffect(() => {
    const headerEl = document.getElementById("header");
    if (!headerEl) return;
    if (document.getElementsByClassName("navigation-menu expanded")?.length) return;
    if (document.getElementsByClassName("modal fade show")?.length) {
      headerEl.classList.add("hidden");
      return;
    }
    if (prevScrollpos?.current > pageYOffset || pageYOffset < 1 || !pageYOffset) {
      headerEl.classList.remove("hidden");
    } else {
      headerEl.classList.add("hidden");
    }
    prevScrollpos.current = pageYOffset;
  }, [pageYOffset, prevScrollpos]);

  /* ── back button redirect ── */
  const handleClickForBackButton = (param) => (e) => {
    e.preventDefault();
    const detailsPage = localStorage.getItem("redirect");
    if (detailsPage) {
      localStorage.removeItem("redirect");
      navigate(detailsPage);
    } else if (param) {
      navigate(param);
    } else {
      navigate(HOME_PATH);
    }
  };

  /* ── generic click handler ── */
  const handleClick = (param) => (e) => {
    e.preventDefault();

    if (logoutRequiredFor.indexOf(param) > -1) {
      dispatch(logoutAction({ source: "handleClick" }));
    }
    if (param === "registervendor") {
      dispatch(logoutAction({ source: "handleClick" }));
    }
    if (param === "home") return navigate(backToHomePath);
    if (param === "notification") return navigate(NOTIFCATION_PATH);

    setIsNavExpanded((prev) => !prev);
  };

  /* ── close drawer ── */
  const closeDrawer = () => setIsNavExpanded(false);

  /* ── drawer item click: navigate + close ── */
  const drawerNav = (path, extra) => (e) => {
    e.preventDefault();
    if (extra === "logout") dispatch(logoutAction({ source: "drawer" }));
    if (extra === "registervendor") dispatch(logoutAction({ source: "drawer" }));
    closeDrawer();
    navigate(path);
  };

  /* ── avatar initials fallback ── */
  const initials = authData?.name
    ? authData.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "U";

  return (
    <>
      {/* ═══════════════════════════════════════
          Top navigation bar
      ═══════════════════════════════════════ */}
      <nav className="navigation justify-content-between px-3" id="header">

        {/* Left: back button (mobile) or spacer */}
        {screenSize.isMobileView ? (
          <div>
            <a className="heading__link text-truncate" onClick={handleClickForBackButton(header?.gotoAction)}>
              {!hideBackButton && <CustomIcon type="chevron-left" />}
              <span className="vertical_align_middle">{header?.gotoName}</span>
            </a>
          </div>
        ) : (
          <div>&nbsp;</div>
        )}

        {/* Center: page name (mobile only) */}
        {screenSize.isMobileView ? (
          <div className="text-capitalize col-4 text-truncate text-center">
            {header?.currentPageName}
          </div>
        ) : (
          <div>&nbsp;</div>
        )}

        {/* Right: bell + avatar OR join button (mobile) */}
        {screenSize.isMobileView && authData?.isLoggedIn ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {/* Notification bell */}
            <div className="heading__link" onClick={handleClick("notification")}>
              <div className="notification-bell">
                <svg className="notification-bell-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <CustomIcon type="bell" />
                </svg>
                <span className="notification-dot"></span>
              </div>
            </div>

            {/* Avatar — opens drawer */}
            <div
              className="heading__link my-color-bg-light-1 animated-border"
              onClick={() => setIsNavExpanded((prev) => !prev)}
            >
              <CustomImage
                className="thumbnail circle"
                state={{ image, name: authData?.name }}
                size="2.3rem"
              />
            </div>
          </div>
        ) : null}

        {screenSize.isMobileView && !authData?.isLoggedIn ? (
          <button
            className="btn btn-warning btn-sm animated-pulse-effect"
            onClick={() => setIsNavExpanded((prev) => !prev)}
          >
            Join
          </button>
        ) : null}

        {/* Desktop nav links (unchanged) */}
        {!screenSize.isMobileView && (
          <div className={isNavExpanded ? "navigation-menu expanded" : "navigation-menu"}>
            <ul>
              <li onClick={handleClick("home")}>
                <NavLink to={backToHomePath}>Home</NavLink>
              </li>
              {!authData?.isLoggedIn && (
                <li onClick={handleClick("register")}>
                  <NavLink to={REGISTER_PATH}>Register / Login</NavLink>
                </li>
              )}
              {authData?.isLoggedIn && (
                <li onClick={handleClick("profile")}>
                  <NavLink to={PROFILE_PATH}>Profile</NavLink>
                </li>
              )}

              {authData?.isLoggedIn && authData?.role === "producer" && (
                <li onClick={handleClick("subscription")}>
                  <NavLink to={SUBSCRIPTION_PATH}>Subscription</NavLink>
                </li>
              )}
              {authData?.isLoggedIn && (authData?.role === "consumer" || authData?.isAdmin) && (
                <li onClick={handleClick("Favourites")}>
                  <NavLink to={FAVOURITES_PATH}>Favourites</NavLink>
                </li>
              )}
              {authData?.isLoggedIn && (
                <li onClick={handleClick("notification")}>
                  <NavLink to={NOTIFCATION_PATH}>Notifications</NavLink>
                </li>
              )}
              <li onClick={handleClick("contactus")}>
                <NavLink to={CONTACT_US_PATH}>Contact Us</NavLink>
              </li>
              {!authData?.isLoggedIn && (
                <li onClick={handleClick("aboutus")}>
                  <NavLink to={ABOUT_US_PATH}>About Us</NavLink>
                </li>
              )}
              <li onClick={handleClick("tnc")}>
                <NavLink to={TNC_PATH}>Terms &amp; Conditions</NavLink>
              </li>
              {authData?.isLoggedIn && (
                <li onClick={handleClick("logout")}>
                  <NavLink to={LOGOUT_PATH}>Logout</NavLink>
                </li>
              )}
            </ul>
          </div>
        )}
      </nav>

      {/* ═══════════════════════════════════════
          Mobile Drawer (slide-in from right)
      ═══════════════════════════════════════ */}
      {screenSize.isMobileView && (
        <>
          {/* Backdrop */}
          <div
            className={`mob-nav-overlay ${isNavExpanded ? "open" : ""}`}
            onClick={closeDrawer}
          />

          {/* Drawer panel */}
          <div className={`mob-nav ${isNavExpanded ? "open" : ""}`}>

            {/* ── Header ── */}
            <div className="mob-nav__header">
              <div className="mob-nav__user">
                <div className="mob-nav__avatar">
                  {authData?.isLoggedIn ? (
                    image ? (
                      <img src={image} alt={authData?.name} />
                    ) : (
                      initials
                    )
                  ) : (
                    <i className="fa fa-user" />
                  )}
                </div>
                <div>
                  {authData?.isLoggedIn ? (
                    <>
                      <p className="mob-nav__welcome capitalize">
                        {authData?.name || "Welcome"}
                      </p>
                      <p className="mob-nav__sub">
                        {roleLabel && (
                          <span style={{ color: getTransactionStatusStyle(roleLabel.status)?.pillBg }}>
                            {roleLabel.text}
                          </span>
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mob-nav__welcome">Welcome!</p>
                      <p className="mob-nav__sub">Sign in to get started</p>
                    </>
                  )}
                </div>
              </div>
              <button className="mob-nav__close" onClick={closeDrawer} aria-label="Close menu">
                ✕
              </button>
            </div>

            {/* ── Scrollable menu ── */}
            <div className="mob-nav__menu">

              {/* Main section */}
              <p className="mob-nav__section-label">Main</p>

              <DrawerItem
                iconClass="home"
                emoji="🏠"
                label="Home"
                to={backToHomePath}
                active={location.pathname === HOME_PATH || location.pathname === HOME_SEARCH_PATH}
                onClick={drawerNav(backToHomePath)}
              />

              {!authData?.isLoggedIn && (
                <DrawerItem
                  iconClass="register"
                  emoji="👤"
                  label="Register / Login"
                  to={REGISTER_PATH}
                  onClick={drawerNav(REGISTER_PATH)}
                />
              )}

              {authData?.isLoggedIn && (
                <DrawerItem
                  iconClass="profile"
                  emoji="👤"
                  label="Profile"
                  to={PROFILE_PATH}
                  active={location.pathname === PROFILE_PATH}
                  onClick={drawerNav(PROFILE_PATH)}
                />
              )}

              {authData?.isLoggedIn && (
                <DrawerItem
                  iconClass="notification"
                  emoji="🔔"
                  label="Notifications"
                  to={NOTIFCATION_PATH}
                  active={location.pathname === NOTIFCATION_PATH}
                  onClick={drawerNav(NOTIFCATION_PATH)}
                />
              )}

              {authData?.isLoggedIn && (authData?.role === "consumer" || authData?.isAdmin) && (
                <DrawerItem
                  iconClass="favourites"
                  emoji="❤️"
                  label="Favourites"
                  to={FAVOURITES_PATH}
                  active={location.pathname === FAVOURITES_PATH}
                  onClick={drawerNav(FAVOURITES_PATH)}
                />
              )}

              <div className="mob-nav__divider" />
              <p className="mob-nav__section-label">Account</p>

              {authData?.isLoggedIn && authData?.role === "producer" && (
                <DrawerItem
                  iconClass="subscription"
                  emoji="💳"
                  label="Subscription"
                  to={SUBSCRIPTION_PATH}
                  active={location.pathname === SUBSCRIPTION_PATH}
                  onClick={drawerNav(SUBSCRIPTION_PATH)}
                />
              )}

              <DrawerItem
                iconClass="contact"
                emoji="📬"
                label="Contact Us"
                to={CONTACT_US_PATH}
                active={location.pathname === CONTACT_US_PATH}
                onClick={drawerNav(CONTACT_US_PATH)}
              />

              {!authData?.isLoggedIn && (
                <DrawerItem
                  iconClass="about"
                  emoji="ℹ️"
                  label="About Us"
                  to={ABOUT_US_PATH}
                  active={location.pathname === ABOUT_US_PATH}
                  onClick={drawerNav(ABOUT_US_PATH)}
                />
              )}

              <DrawerItem
                iconClass="tnc"
                emoji="📄"
                label="Terms &amp; Conditions"
                to={TNC_PATH}
                active={location.pathname === TNC_PATH}
                onClick={drawerNav(TNC_PATH)}
              />

              {/* ── Admin section ── */}
              {authData?.isLoggedIn && authData?.isAdmin && (
                <>
                  <div className="mob-nav__divider" />
                  <p className="mob-nav__section-label">Admin</p>

                  <DrawerItem
                    iconClass="admin"
                    emoji="🏷️"
                    label="Register Vendor"
                    to={REGISTER_GENERAL_PATH}
                    active={location.pathname === REGISTER_GENERAL_PATH}
                    onClick={drawerNav(REGISTER_GENERAL_PATH, "registervendor")}
                  />
                  <DrawerItem
                    iconClass="admin"
                    emoji="👥"
                    label="User Management"
                    to={USER_MANAGEMENT_PATH}
                    active={location.pathname === USER_MANAGEMENT_PATH}
                    onClick={drawerNav(USER_MANAGEMENT_PATH)}
                  />
                  <DrawerItem
                    iconClass="admin"
                    emoji="📋"
                    label="Contact Us Management"
                    to={CONTACT_US_MANAGEMENT_PATH}
                    active={location.pathname === CONTACT_US_MANAGEMENT_PATH}
                    onClick={drawerNav(CONTACT_US_MANAGEMENT_PATH)}
                  />
                  <DrawerItem
                    iconClass="admin"
                    emoji="📊"
                    label="Analytics Management"
                    to={ANALYTICS_MANAGEMENT_PATH}
                    active={location.pathname === ANALYTICS_MANAGEMENT_PATH}
                    onClick={drawerNav(ANALYTICS_MANAGEMENT_PATH)}
                  />
                  <DrawerItem
                    iconClass="category"
                    emoji="🗂️"
                    label="Category"
                    to={CATEGORY_PATH}
                    active={location.pathname === CATEGORY_PATH}
                    onClick={drawerNav(CATEGORY_PATH)}
                  />
                  <DrawerItem
                    iconClass="push"
                    emoji="📣"
                    label="Push Notification"
                    to={PUSH_NOTIFICATION_PATH}
                    active={location.pathname === PUSH_NOTIFICATION_PATH}
                    onClick={drawerNav(PUSH_NOTIFICATION_PATH)}
                  />
                  <DrawerItem
                    iconClass="admin"
                    emoji="💼"
                    label="Manage Subscription Plan"
                    to={MANAGE_SUBSCRIPTION_PLAN_PATH}
                    active={location.pathname === MANAGE_SUBSCRIPTION_PLAN_PATH}
                    onClick={drawerNav(MANAGE_SUBSCRIPTION_PLAN_PATH)}
                  />
                  <DrawerItem
                    iconClass="admin"
                    emoji="🔕"
                    label="Manage Notifications"
                    to={MANAGE_NOTIFICATION_PATH}
                    active={location.pathname === MANAGE_NOTIFICATION_PATH}
                    onClick={drawerNav(MANAGE_NOTIFICATION_PATH)}
                  />
                </>
              )}

              {/* ── Logout ── */}
              {authData?.isLoggedIn && (
                <>
                  <div className="mob-nav__divider" />
                  <DrawerItem
                    iconClass="logout"
                    emoji="🚪"
                    label="Logout"
                    to={LOGOUT_PATH}
                    danger
                    onClick={drawerNav(LOGOUT_PATH, "logout")}
                  />
                </>
              )}
            </div>

            {/* ── Social footer ── */}
            <div className="mob-nav__footer">
              <p className="mob-nav__footer-label">Follow Us</p>
              <div className="mob-nav__socials">
                <a
                  href="#"
                  className="mob-nav__social-btn mob-nav__social-btn--insta"
                  onClick={(e) => { e.preventDefault(); handleHrefRedirect("https://www.instagram.com/gaufi.com_/"); }}
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <i className="fa fa-instagram" />
                </a>
                <a
                  href="#"
                  className="mob-nav__social-btn mob-nav__social-btn--fb"
                  onClick={(e) => { e.preventDefault(); handleHrefRedirect("https://www.facebook.com/profile.php?id=61569788811617"); }}
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <i className="fa fa-facebook-f" />
                </a>
                <a
                  href="#"
                  className="mob-nav__social-btn mob-nav__social-btn--play"
                  onClick={(e) => { e.preventDefault(); handleHrefRedirect("https://play.google.com/store/apps/details?id=com.gaufi"); }}
                  rel="noreferrer"
                  aria-label="Google Play"
                >
                  {/* <CustomIcon type="googleplay" /> */}
                  <span>Google Play</span>
                </a>
              </div>
            </div>

            <div className="mob-nav__version">{appVersion}</div>
          </div>
        </>
      )}
    </>
  );
};

export default NavBar;